import React from "react";
import { motion } from "framer-motion";
import {
  FaIndustry,
  FaPlane,
  FaCar,
  FaHeartbeat,
  FaMicrochip,
  FaCogs,
  FaShieldAlt,
  FaAward,
  FaCheckCircle,
  FaRocket,
  FaTools,
  FaWrench,
} from "react-icons/fa";
import dexterImage3 from "../../../assets/dexter-images/dexter-3.jpg";
import dexterImage4 from "../../../assets/dexter-images/dexter-4.jpg";

function IndustryServed() {
  return (
    <div className="service-page">
      <motion.div
        className="service-hero"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="hero-content">
            <h1>Industries We Serve</h1>
            <p>
              Comprehensive Manufacturing Solutions Across Multiple Industries
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="service-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="container">
          {/* Hero Section with Image */}
          <div className="service-hero-section">
            <div className="hero-text">
              <h2>Comprehensive Industry Solutions</h2>
              <p>
                DexteR Technology serves a diverse range of industries with
                precision manufacturing solutions tailored to specific
                requirements. Our expertise spans from aerospace to medical
                devices, providing high-quality components that meet industry
                standards and regulations.
              </p>
              <div className="hero-features">
                <div className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>Industry-Specific Standards</span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>Quality Certified Processes</span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>Custom Solutions</span>
                </div>
              </div>
            </div>
            <div className="hero-image">
              <img src={dexterImage3} alt="Industry Manufacturing Solutions" />
            </div>
          </div>

          {/* Industries Grid */}
          <div className="capabilities-section">
            <h2>Our Industry Expertise</h2>
            <div className="capabilities-grid">
              <motion.div
                className="capability-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="card-icon">
                  <FaPlane />
                </div>
                <h3>Aerospace</h3>
                <p>
                  Precision components for aerospace applications with strict
                  quality standards and certifications
                </p>
                <ul>
                  <li>Engine components</li>
                  <li>Landing gear parts</li>
                  <li>Control systems</li>
                  <li>Structural elements</li>
                </ul>
              </motion.div>

              <motion.div
                className="capability-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="card-icon">
                  <FaCar />
                </div>
                <h3>Automotive</h3>
                <p>
                  Reliable automotive components with high precision and
                  durability for vehicle systems
                </p>
                <ul>
                  <li>Engine parts</li>
                  <li>Transmission components</li>
                  <li>Brake system parts</li>
                  <li>Electrical components</li>
                </ul>
              </motion.div>

              <motion.div
                className="capability-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="card-icon">
                  <FaHeartbeat />
                </div>
                <h3>Medical</h3>
                <p>
                  High-precision medical device components with biocompatible
                  materials and sterile processing
                </p>
                <ul>
                  <li>Surgical instruments</li>
                  <li>Medical device parts</li>
                  <li>Diagnostic equipment</li>
                  <li>Implant components</li>
                </ul>
              </motion.div>

              <motion.div
                className="capability-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="card-icon">
                  <FaMicrochip />
                </div>
                <h3>Electronics</h3>
                <p>
                  Precision parts for electronic applications with conductivity
                  and thermal management
                </p>
                <ul>
                  <li>Circuit board components</li>
                  <li>Heat sinks</li>
                  <li>Connector parts</li>
                  <li>Housing components</li>
                </ul>
              </motion.div>

              <motion.div
                className="capability-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="card-icon">
                  <FaIndustry />
                </div>
                <h3>Industrial Machinery</h3>
                <p>
                  Durable components for industrial machinery with high load
                  capacity and wear resistance
                </p>
                <ul>
                  <li>Gear components</li>
                  <li>Bearing housings</li>
                  <li>Hydraulic parts</li>
                  <li>Pneumatic components</li>
                </ul>
              </motion.div>

              <motion.div
                className="capability-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="card-icon">
                  <FaCogs />
                </div>
                <h3>Custom Manufacturing</h3>
                <p>
                  Specialized solutions for unique requirements with custom
                  design and manufacturing
                </p>
                <ul>
                  <li>Prototype development</li>
                  <li>Custom tooling</li>
                  <li>Specialized parts</li>
                  <li>Assembly services</li>
                </ul>
              </motion.div>
            </div>
          </div>

          {/* Process Image */}
          <div className="process-image-section">
            <motion.div
              className="process-image"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img src={dexterImage4} alt="Industry Manufacturing Process" />
              <div className="image-overlay">
                <div className="overlay-content">
                  <h3>Multi-Industry Manufacturing</h3>
                  <p>
                    Precision components for diverse industries with quality
                    assurance
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Quality Standards Section */}
          {/* <div className="materials-section">
            <h2>Quality Standards by Industry</h2>
            <div className="materials-grid">
              <motion.div
                className="material-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="material-icon">
                  <FaShieldAlt />
                </div>
                <h3>Aerospace Standards</h3>
                <p>
                  AS9100 certified processes with strict quality control and
                  traceability requirements
                </p>
              </motion.div>

              <motion.div
                className="material-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="material-icon">
                  <FaAward />
                </div>
                <h3>Automotive Standards</h3>
                <p>
                  IATF 16949 compliance with automotive-specific quality
                  management systems
                </p>
              </motion.div>

              <motion.div
                className="material-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="material-icon">
                  <FaCheckCircle />
                </div>
                <h3>Medical Standards</h3>
                <p>
                  ISO 13485 certified with FDA compliance for medical device
                  manufacturing
                </p>
              </motion.div>

              <motion.div
                className="material-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="material-icon">
                  <FaRocket />
                </div>
                <h3>Electronics Standards</h3>
                <p>
                  IPC standards compliance with precision requirements for
                  electronic components
                </p>
              </motion.div>

              <motion.div
                className="material-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="material-icon">
                  <FaTools />
                </div>
                <h3>Industrial Standards</h3>
                <p>
                  ISO 9001 certified with industry-specific quality and safety
                  requirements
                </p>
              </motion.div>

              <motion.div
                className="material-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="material-icon">
                  <FaWrench />
                </div>
                <h3>Custom Standards</h3>
                <p>
                  Tailored quality processes designed to meet specific customer
                  requirements
                </p>
              </motion.div>
            </div>
          </div> */}

          {/* Industry Capabilities */}
          {/* <div className="specs-section">
            <h2>Industry-Specific Capabilities</h2>
            <div className="specs-grid">
              <motion.div
                className="spec-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3>Precision Manufacturing</h3>
                <p>
                  Ultra-precise components with tolerances up to 10 microns for
                  critical applications
                </p>
              </motion.div>

              <motion.div
                className="spec-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3>Material Expertise</h3>
                <p>
                  Specialized knowledge in various materials including SS,
                  Aluminum, Brass, and custom alloys
                </p>
              </motion.div>

              <motion.div
                className="spec-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3>Quality Assurance</h3>
                <p>
                  Comprehensive testing and inspection processes with certified
                  quality standards
                </p>
              </motion.div>

              <motion.div
                className="spec-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h3>Custom Solutions</h3>
                <p>
                  Tailored manufacturing solutions designed to meet specific
                  industry requirements
                </p>
              </motion.div>
            </div>
          </div> */}

          {/* Why Choose Us Section */}
          {/* <div className="why-choose-section">
            <h2>Why Industries Choose DexteR Technology</h2>
            <div className="benefits-grid">
              <motion.div
                className="benefit-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <FaCheckCircle className="benefit-icon" />
                <h3>Industry Expertise</h3>
                <p>
                  Deep understanding of industry-specific requirements and
                  quality standards
                </p>
              </motion.div>

              <motion.div
                className="benefit-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <FaCheckCircle className="benefit-icon" />
                <h3>Quality Certified</h3>
                <p>
                  Multiple industry certifications ensuring compliance with
                  quality standards
                </p>
              </motion.div>

              <motion.div
                className="benefit-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <FaCheckCircle className="benefit-icon" />
                <h3>Custom Solutions</h3>
                <p>
                  Tailored manufacturing processes designed for specific
                  industry needs
                </p>
              </motion.div>

              <motion.div
                className="benefit-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <FaCheckCircle className="benefit-icon" />
                <h3>Reliable Delivery</h3>
                <p>
                  Consistent on-time delivery with quality assurance and
                  traceability
                </p>
              </motion.div>
            </div>
          </div> */}
        </div>
      </motion.div>
    </div>
  );
}

export default IndustryServed;
